// Whole-establishment overview for group management.
// Shows every campus × filière (options indented) × level with student counts
// and a grand total, so the user can always see where all students sit.
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Users, LayoutGrid, AlertCircle } from 'lucide-react';
import { getGroupsOverview, type GroupsOverview } from '@/services/groupsService';
import { getNiveauLabel } from '@/types/group.types';

interface Props {
  academicYearId: string;
  /** Bump after every mutation so the totals follow the latest changes. */
  refreshKey?: number;
}

const GroupsOverviewPanel: React.FC<Props> = ({ academicYearId, refreshKey = 0 }) => {
  const [data, setData] = useState<GroupsOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async (): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getGroupsOverview(academicYearId);
        if (active) setData(result);
      } catch (err) {
        if (active) {
          setData(null);
          setError(err instanceof Error ? err.message : "Impossible de charger la vue d'ensemble");
        }
      } finally {
        if (active) setIsLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [academicYearId, refreshKey]);

  const grandTotal = data?.grandTotal ?? 0;
  const totalAssigned =
    data?.campuses.reduce(
      (sum, c) => sum + c.filieres.reduce((s, f) => s + f.assigned, 0),
      0,
    ) ?? 0;
  const totalPending = grandTotal - totalAssigned - (data?.uncategorized ?? 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header / toggle */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
          <LayoutGrid className="h-4 w-4 text-primary-600" />
          <span className="font-medium text-gray-900">Vue d'ensemble — tous campus & filières</span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
          ) : error ? (
            <span className="flex items-center gap-1 text-red-600">
              <AlertCircle className="h-4 w-4" /> Erreur de chargement
            </span>
          ) : (
            <>
              <span className="text-gray-500">
                <span className="font-semibold text-gray-900">{grandTotal}</span> étudiants
              </span>
              <span className="text-primary-600 hidden sm:inline">{totalAssigned} assignés</span>
              <span className="text-amber-600 hidden sm:inline">{totalPending} en attente</span>
            </>
          )}
        </div>
      </button>

      {isOpen && !isLoading && error && (
        <div className="border-t border-gray-100 p-4 text-sm text-red-700 bg-red-50">{error}</div>
      )}

      {/* Body */}
      {isOpen && !isLoading && data && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {data.uncategorized > 0 && (
            <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 rounded-lg">
              {data.uncategorized} étudiant(s) non catégorisé(s) (campus ou filière manquant/invalide) — non
              affiché(s) dans le détail ci-dessous.
            </div>
          )}

          {data.campuses.map((campus) => (
            <div key={campus.campusId} className="rounded-lg border border-gray-100">
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-t-lg">
                <span className="text-sm font-semibold text-gray-900">{campus.campusName}</span>
                <span className="text-xs text-gray-500">
                  <Users className="h-3 w-3 inline mr-1" />
                  {campus.total}
                </span>
              </div>
              <div className="divide-y divide-gray-50">
                {campus.filieres.map((fil) => {
                  const pending = fil.total - fil.assigned;
                  return (
                    <div key={`${campus.campusId}-${fil.filiereId}`} className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-sm ${fil.isOption ? 'text-gray-600 pl-4' : 'font-medium text-gray-900'}`}
                        >
                          {fil.isOption && <span className="text-gray-300 mr-1">▸</span>}
                          {fil.isOption ? fil.optionName || fil.name : fil.name}
                        </span>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {fil.total}{' '}
                          <span className="text-primary-600">({fil.assigned} assignés</span>
                          {pending > 0 && <span className="text-amber-600">, {pending} en attente</span>}
                          <span className="text-primary-600">)</span>
                        </span>
                      </div>
                      {/* Per-level chips */}
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {fil.niveaux.map((n) => {
                          const total = n.fiCount + n.fcCount;
                          const pend = n.fiPending + n.fcPending;
                          return (
                            <span
                              key={n.niveau}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600"
                              title={`FI ${n.fiCount} / FC ${n.fcCount}`}
                            >
                              {getNiveauLabel(n.niveau)}: {total}
                              {pend > 0 && <span className="text-amber-600">({pend} en attente)</span>}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GroupsOverviewPanel;
